import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import useQueryStringParameters from '../../utils/useQueryStringParameters';
import { gscConnectionApi } from './api/gscConnectionApi';
import { resolveSelectedProperty, toSelectableProperties } from './SearchPerformanceProperties';
import {
  buildSearchPerformanceQueryParameters,
  type SearchPerformanceQueryParams,
} from './SearchPerformanceQueryParameters';

export type SearchPerformanceCommonEventProperties = {
  /** Raw selected property id, e.g. `sc-domain:example.com`. Empty before a domain is connected. */
  domain: string;
  /** Active/synced GSC domains, 0–5. Pro is one, Enterprise Plus is many (PRE-3271 decision). */
  connected_domains_count: number;
};

/**
 * The two per-event properties PRE-3271 attaches to every Search Performance
 * event that are not added server-side (`company_id` is). The tracker merges
 * these into every event; nothing else calls this directly.
 *
 * Reads the cached `/gsc/status` and never triggers a fetch, so tracking an event
 * does not pull that request onto the surface that fires it.
 */
const selectGscConnectionStatus = gscConnectionApi.endpoints.getGscConnectionStatus.select();

function useSearchPerformanceCommonEventProperties(): SearchPerformanceCommonEventProperties {
  const { data } = useSelector(selectGscConnectionStatus);
  const { queryStrings } = useQueryStringParameters<SearchPerformanceQueryParams>(
    buildSearchPerformanceQueryParameters
  );

  return useMemo(() => {
    const sources = data?.sources ?? [];
    const selected = resolveSelectedProperty(toSelectableProperties(sources), queryStrings.property);
    const connectedDomainsCount = sources.filter(
      (source) => source.type === 'GSC' && source.status === 'ACTIVE' && source.propertyUrl !== null
    ).length;
    return {
      domain: selected?.propertyUrl ?? '',
      connected_domains_count: connectedDomainsCount,
    };
  }, [data, queryStrings.property]);
}

export default useSearchPerformanceCommonEventProperties;
